/**
 * WEB-2054 (починка ревизии, Р1) — ЧТЕНИЕ ПОЛЕЙ «ИИ-ФОРМЫ» БЛОКА, ОДНОЙ КОПИЕЙ.
 *
 * Жило внутри `PublicBlockRendererExtended.tsx`, пока рендерер был один. После
 * того как денежные и частые виды переехали в ядро
 * (`PublicBlockRendererCommon.tsx`), это чтение понадобилось ОБОИМ файлам —
 * значит оно обязано быть одним, а не копией на каждый. Копия здесь особенно
 * опасна: список полей растёт с каждым новым видом блока ИИ-воронки, и
 * разошедшиеся копии молча теряли бы поле в одном из рендереров.
 */
import type {
  EditorBlock,
  PlanSelectorPlan,
  ImageChoiceOption,
} from '@/store/editorSlice';
import type { DownsellTriggerName } from './downsellTriggerContext';

/**
 * WEB-447 Волна 0 — field-name mismatch between the AI generator and the
 * editor factory. The backend Zod schemas (`ai-funnels/block-schema/*`) emit
 * short field names (`plans`, `template`, `question`, ...); the editor's
 * `createDefaultBlock` (editorSlice.ts) writes prefixed names
 * (`planSelectorPlans`, `paymentTermsTemplate`, ...). No adapter exists
 * between the two, so a persisted block may carry either shape depending on
 * which path created it. This type documents the AI-side field names for
 * the 8 blocks below so the renderer can read both defensively without
 * touching the editor's EditorBlock type (renderer-only fix).
 */
export type AiShapedFields = {
  // plan_selector
  plans?: PlanSelectorPlan[];
  layout?: 'cards' | 'list';
  defaultPriceId?: string;
  // payment_terms
  template?: string;
  // payment_terms + downsell_popup share `priceId`
  priceId?: string;
  // downsell_popup
  title?: string;
  text?: string;
  ctaLabel?: string;
  // WEB-1500: условие показа скидки. Без этого поля в типе оно терялось на
  // типизации — и ИИ-воронка показывала удерживающее предложение всем сразу.
  triggers?: DownsellTriggerName[];
  // image_choice / weight_picker / height_picker / photo_upload
  question?: string;
  options?: ImageChoiceOption[];
  multiple?: boolean;
  unitDefault?: string;
  min?: number;
  max?: number;
  // cookie_consent
  acceptLabel?: string;
  declineLabel?: string;
  policyUrl?: string;
  position?: 'bottom' | 'modal';
  // photo_upload
  maxSizeMb?: number;
  accept?: string[];
};

/** Reads AI-shaped fallback fields off a block without widening EditorBlock. */
export function aiFields(block: EditorBlock): AiShapedFields {
  return block as unknown as AiShapedFields;
}
